export interface UserRole {
  id: string;
  name: string;
  permissions?: any;
  is_internal?: boolean;
}

export enum PermissionGrants {
  CanView = 'can_view',
  CanEdit = 'can_edit',
  CanRemove = 'can_remove',

  // TODO: Verify if we need document permissions
  CanDownload = 'can_download',
  CanUpload = 'can_upload',
}

export interface RolePermission {
  id?: string;
  name?: string;
  role_id?: string;
  [PermissionGrants.CanView]: boolean;
  [PermissionGrants.CanEdit]: boolean;
  [PermissionGrants.CanRemove]: boolean;
}

export enum PermissionLevel {
  Study = 'study',
  Site = 'site',
  Org = 'organization',
  Workspace = 'workspace',
}

export enum Role {
  // Study roles
  StudyManager = 'Study Manager',
  StudySubmitter = 'Study Submitter',
  StudyReadOnly = 'Study Visibility',

  // Site roles
  SiteParticipant = 'Site Participant',
  SiteManager = 'Site Manager',
  SiteSubmitter = 'Site Submitter',
  SiteVisibility = 'Site Visibility',

  // User level roles
  External = 'External',
  Staff = 'Staff',
  Manager = 'Manager',
  BusinessAdmin = 'Business Admin',
  ITAdmin = 'IT Admin',
}

export enum PermissionNames {
  // Study Permissions
  StudyDetail = 'Study Detail',
  SiteStudyDetail = 'Site Study Detail',

  // Site Permissions
  SiteDetail = 'Site Detail',

  // Submission Permissions
  StudySubmission = 'Study Submission',
  SiteSubmission = 'Site Submission',
  NewSiteSubmission = 'New Site Submission',
  AppealSubmission = 'Appeal Submission',
  ReassignSubmission = 'Reassign Submission',
  CancelSubmission = 'Cancel Submission',

  // Collabhub Permissions
  StudyCorrespondence = 'Study Correspondence',
  SiteCorrespondence = 'Site Correspondence',
  SubmissionCorrespondence = 'Submission Correspondence Access',

  // Workspace Permissions
  QuestionnaireAccess = 'Questionnaire Access',
  SubmissionDocumentAccess = 'Submission Document Access',
  UpdateSubmissionPriority = 'Update Submission Priority',
  UpdateSubmissionTeam = 'Update Submission Team',

  // Team Permissions
  StudyTeam = 'Study Team',
  SiteTeam = 'Site Team',

  // Admin Permissions
  PeopleAdmin = 'People Admin',
  RoleAdmin = 'Role Admin',
  NotificationAdmin = 'Notification Admin',
  QuestionnaireAdmin = 'Questionnaire Admin',
  ConfigurationAdmin = 'Configuration Admin',
  AuditAdmin = 'Audit Admin',
  ImpersonateExternalUser = 'Impersonate External User',
  ImpersonateInternalUser = 'Impersonate Internal User',

  // Super Admin Permissions
  SubmissionSuperAdmin = 'Submission Super Admin',
  DocumentSuperAdmin = 'Document Super Admin',

  // Organization Permissions
  organization = 'Overview',
  people = 'People',

  ViewOrganizationEnum = 'View Organization',
  ViewOrganizationDetailEnum = 'View Organization Details',
  AddOrganizationAdminUserEnum = 'Add Organization Admin Users',
  RemoveOrganizationAdminUserEnum = 'Remove Organization Admin User',
  ViewOrganizationAdminUserEnum = 'View Org Admin Users',
  ViewOrganizationStudiesEnum = 'View Org Studies',
  ViewOrganizationSitesEnum = 'View Org Sites',
  ViewOrgDetailsEnum = 'View Org Details',
}

export enum PermissionGroupTypes {
  studyPermission = 'Study Permissions',
  sitePermission = 'Site Permissions',
  submissionPermission = 'Submission Permissions',
  collabhubPermission = 'Collabhub Permissions',
  teamManagement = 'Team Management',
  adminPermission = 'Administrative',
  submissionSuperAdminPermission = 'Submission Super Admin Permissions',
  organizationPermission = 'Organization Permissions',
  organizationInformation = 'Organization Information',
  organizationMembers = 'Organization Members',

  organizationDetailsPermissions = 'Organization Overview',
  organizationUserManagementPermissions = 'Organization User Management',
  organizationAccessPermissions = 'Organization Resource Access',
}

export abstract class BasePermission {
  abstract level: PermissionLevel;
  abstract name: PermissionNames;
  abstract grants: PermissionGrants[];
}

/**
 * @ORGANIZATION PERMISSIONS
 * */
export class OrganizationPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.organization;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class OrganizationPeoplePermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.people;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

/**
 * Base Permissions for the system
 */
export class StudyDetailPermission extends BasePermission {
  level = PermissionLevel.Study;
  name = PermissionNames.StudyDetail;
  grants = [PermissionGrants.CanView];
}

export class SiteStudyDetailPermission extends BasePermission {
  level = PermissionLevel.Site;
  name = PermissionNames.SiteStudyDetail;
  grants = [PermissionGrants.CanView];
}

export class SiteDetailPermission extends BasePermission {
  level = PermissionLevel.Site;
  name = PermissionNames.SiteDetail;
  grants = [PermissionGrants.CanView];
}

//This relates to the study itself and the permissions surrounding editing the study and viewing the study
export class StudySubmissionPermission extends BasePermission {
  level = PermissionLevel.Study;
  name = PermissionNames.StudySubmission;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

//This relates to the site itself and the permissions surrounding editing the site and viewing the site
export class SiteSubmissionPermission extends BasePermission {
  level = PermissionLevel.Site;
  name = PermissionNames.SiteSubmission;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class NewSiteSubmissionPermission extends BasePermission {
  level = PermissionLevel.Site;
  name = PermissionNames.NewSiteSubmission;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class AppealSubmissionPermission extends BasePermission {
  level = PermissionLevel.Study;
  name = PermissionNames.AppealSubmission;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class QuestionnaireAccessPermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.QuestionnaireAccess;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}

export class UpdateSubmissionPriorityPermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.UpdateSubmissionPriority;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}

export class UpdateSubmissionTeamPermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.UpdateSubmissionTeam;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class SubmissionDocumentAccessPermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.SubmissionDocumentAccess;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}

export class ReassignSubmissionPermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.ReassignSubmission;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}

export class CancelSubmissionPermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.CancelSubmission;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}

export class StudyCorrespondencePermission extends BasePermission {
  level = PermissionLevel.Study;
  name = PermissionNames.StudyCorrespondence;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class SubmissionCorrespondencePermission extends BasePermission {
  level = PermissionLevel.Workspace;
  name = PermissionNames.SubmissionCorrespondence;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}

export class SiteCorrespondencePermission extends BasePermission {
  level = PermissionLevel.Site;
  name = PermissionNames.SiteCorrespondence;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

//This relates to the permissions surrounding editing the study team and viewing the study team
export class StudyTeamPermission extends BasePermission {
  level = PermissionLevel.Study;
  name = PermissionNames.StudyTeam;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

//This relates to the permissions surrounding editing the site team and viewing the site team.
export class SiteTeamPermission extends BasePermission {
  level = PermissionLevel.Site;
  name = PermissionNames.SiteTeam;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class PeopleAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.PeopleAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class RoleAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.RoleAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class ConfigurationAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ConfigurationAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class AuditAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.AuditAdmin;
  grants = [PermissionGrants.CanEdit];
}

export class ImpersonateExternalUserPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ImpersonateExternalUser;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class ImpersonateInternalUserPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ImpersonateInternalUser;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class NotificationAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.NotificationAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class QuestionnaireAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.QuestionnaireAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}

export class SubmissionSuperAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.SubmissionSuperAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}
export class DocumentSuperAdminPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.DocumentSuperAdmin;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}
export class ViewOrganizationPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ViewOrganizationEnum;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}
export class ViewOrganizationDetailPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ViewOrganizationDetailEnum;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}
export class AddOrganizationAdminUserPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.AddOrganizationAdminUserEnum;
  grants = [PermissionGrants.CanView, PermissionGrants.CanEdit];
}
export class RemoveOrganizationAdminUserPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.RemoveOrganizationAdminUserEnum;
  grants = [
    PermissionGrants.CanView,
    PermissionGrants.CanEdit,
    PermissionGrants.CanRemove,
  ];
}
export class ViewOrganizationAdminUserPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ViewOrganizationAdminUserEnum;
  grants = [PermissionGrants.CanView];
}
export class ViewOrganizationStudiesPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ViewOrganizationStudiesEnum;
  grants = [PermissionGrants.CanView];
}
export class ViewOrganizationSitesPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ViewOrganizationSitesEnum;
  grants = [PermissionGrants.CanView];
}

export class ViewOrgDetailsPermission extends BasePermission {
  level = PermissionLevel.Org;
  name = PermissionNames.ViewOrgDetailsEnum;
  grants = [PermissionGrants.CanView];
}

export const StudyDetail = new StudyDetailPermission();
export const SiteStudyDetail = new SiteStudyDetailPermission();
export const SiteDetail = new SiteDetailPermission();
export const StudySubmission = new StudySubmissionPermission();
export const SiteSubmission = new SiteSubmissionPermission();
export const NewSiteSubmission = new NewSiteSubmissionPermission();
export const StudyCorrespondence = new StudyCorrespondencePermission();
export const SiteCorrespondence = new SiteCorrespondencePermission();
export const ReassignSubmission = new ReassignSubmissionPermission();
export const StudyTeam = new StudyTeamPermission();
export const SiteTeam = new SiteTeamPermission();
export const QuestionnaireAccess = new QuestionnaireAccessPermission();
export const SubmissionDocumentAccess =
  new SubmissionDocumentAccessPermission();
export const UpdateSubmissionPriority =
  new UpdateSubmissionPriorityPermission();
export const UpdateSubmissionTeam = new UpdateSubmissionTeamPermission();

//INTERNAL PERMISSIONS
export const PeopleAdmin = new PeopleAdminPermission();
export const RoleAdmin = new RoleAdminPermission();
export const NotificationAdmin = new NotificationAdminPermission();
export const QuestionnaireAdmin = new QuestionnaireAdminPermission();
export const ConfigurationAdmin = new ConfigurationAdminPermission();
export const DocumentSuperAdmin = new DocumentSuperAdminPermission();
export const OrganizationAdmin = new OrganizationPermission();
export const AuditAdmin = new AuditAdminPermission();
export const SubmissionSuperAdmin = new SubmissionSuperAdminPermission();
export const ImpersonateExternalUser = new ImpersonateExternalUserPermission();
export const ImpersonateInternalUser = new ImpersonateInternalUserPermission();
export const AppealSubmission = new AppealSubmissionPermission();
export const CancelSubmission = new CancelSubmissionPermission();

// Organization Permissions
export const ViewOrganization = new ViewOrganizationPermission();
export const ViewOrganizationDetail = new ViewOrganizationDetailPermission();
export const AddOrganizationAdminUser =
  new AddOrganizationAdminUserPermission();
export const RemoveOrganizationAdminUser =
  new RemoveOrganizationAdminUserPermission();
export const ViewOrganizationAdminUser =
  new ViewOrganizationAdminUserPermission();
export const ViewOrganizationStudies = new ViewOrganizationStudiesPermission();
export const ViewOrganizationSites = new ViewOrganizationSitesPermission();

export type PermissionType = {
  can_remove: 'can_remove';
  can_edit: 'can_edit';
  can_view: 'can_view';
};
